import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Building2,
    FlaskConical,
    GraduationCap,
    Network,
    HandCoins,
    Users,
    UserPlus,
    ArrowDown,
    CheckCircle2,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { FoundingSection } from "../components/FoundingSection";
import { SupportSection } from "../components/SupportSection";
import { Reveal, Eyebrow } from "../components/primitives";
import { RegisterForm } from "../components/RegisterForm";
import { API, INTEREST_AREAS } from "../lib/site";

const OPTIONS = [
    { icon: Building2, area: "Hotels", desc: "Join future GGH pilot programs." },
    { icon: FlaskConical, area: "Researchers", desc: "Collaborate on hospitality sustainability research." },
    { icon: GraduationCap, area: "Students", desc: "Participate in learning and sustainability projects." },
    { icon: Network, area: "Organizations", desc: "Collaborate on sustainability initiatives." },
    { icon: HandCoins, area: "Sponsors", desc: "Support GGH research and pilot programs." },
    { icon: Users, area: "Communities", desc: "Participate in responsible tourism and local sustainability initiatives." },
];

const initialForm = { name: "", email: "", organization: "", interest_area: "", message: "" };

const GetInvolved = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const location = useLocation();

    useEffect(() => {
        if (location.hash === "#interest-form") {
            const t = setTimeout(() => {
                document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 400);
            return () => clearTimeout(t);
        }
    }, [location.hash]);

    const pickArea = (area) => {
        setForm((f) => ({ ...f, interest_area: area }));
        setErrors((e) => ({ ...e, interest_area: undefined }));
        document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
        if (!form.interest_area) er.interest_area = "Please choose an area of interest.";
        return er;
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("submitting");
        try {
            await axios.post(`${API}/interest`, form);
            setStatus("success");
            toast.success("Thank you — your interest has been received.");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong. Please try again or email us directly.");
        }
    };

    return (
        <main>
            <SEO
                title="Get Involved | Global Green Hotel"
                description="Hotels, researchers, students, organizations, sponsors and communities — find your place in the future of sustainable hospitality."
            />
            <PageHero
                testid="get-involved-hero"
                eyebrow="Get Involved"
                titleLines={["There is a place", "for you in this work"]}
                description="GGH is growing carefully and collaboratively. Choose how you would like to take part."
            />

            <FoundingSection anchorLink />

            <section data-testid="involve-options" className="py-24 sm:py-32">
                <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="involve-grid">
                        {OPTIONS.map(({ icon: Icon, area, desc }, i) => (
                            <Reveal key={area} delay={0.06 * i} y={24}>
                                <article
                                    data-testid={`involve-${area.toLowerCase()}`}
                                    className="card-lift flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-8 transition-colors duration-300 hover:border-[#1A3B2B]"
                                >
                                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                        <Icon size={24} aria-hidden="true" />
                                    </span>
                                    <h3 className="font-display mt-8 text-2xl font-medium tracking-tight text-[#0A1711]">
                                        {area}
                                    </h3>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                                    <button
                                        type="button"
                                        data-testid={`involve-cta-${area.toLowerCase()}`}
                                        onClick={() => pickArea(area)}
                                        className="mt-8 inline-flex items-center gap-2 self-start text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]"
                                    >
                                        Express Interest
                                        <ArrowDown size={15} aria-hidden="true" />
                                    </button>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="register-section" className="bg-[#F2EFE7] py-24 sm:py-32">
                <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-32">
                            <Reveal>
                                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                    <UserPlus size={24} aria-hidden="true" />
                                </span>
                                <Eyebrow className="mt-6">Join the Movement</Eyebrow>
                                <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                                    Register as an individual contributor
                                </h2>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                    You don't need to represent a hotel or an organization to be
                                    part of Global Green Hotel. If you believe hospitality can be
                                    better for the planet, for people and for business — register
                                    below.
                                </p>
                                <p className="mt-5 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                    Tell us who you are, why you want to participate, and how you
                                    would like to contribute — your time and skills, research and
                                    knowledge, funding or sponsorship, professional expertise, or
                                    advocacy. You may attach a supporting document (such as a CV or
                                    proposal) if you wish. Every registration is sent directly to
                                    the GGH team.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="lg:col-span-7" id="register-form">
                        <RegisterForm />
                    </div>
                </div>
            </section>

            <section id="interest-form" data-testid="interest-section" className="grain relative scroll-mt-24 overflow-hidden bg-[#07211A] py-24 sm:py-32">
                <div className="relative z-[2] mx-auto max-w-[820px] px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow dark>Register Your Interest</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                            Tell us how you would like to be involved
                        </h2>
                    </Reveal>

                    {status === "success" ? (
                        <Reveal delay={0.1}>
                            <div
                                data-testid="interest-success"
                                role="status"
                                className="mt-10 rounded-2xl border border-[#C2A160]/40 bg-[#04150D] p-10"
                            >
                                <CheckCircle2 size={36} className="text-[#D9B96A]" aria-hidden="true" />
                                <p className="font-display mt-5 text-2xl text-[#FDFBF7]">Thank you.</p>
                                <p className="mt-3 text-sm leading-relaxed text-[#FDFBF7]/70">
                                    Thank you for contacting Global Green Hotel. We have received
                                    your message and will respond as soon as possible.
                                </p>
                                <button
                                    type="button"
                                    data-testid="interest-another"
                                    onClick={() => {
                                        setForm(initialForm);
                                        setStatus("idle");
                                    }}
                                    className="btn-outline-light mt-8"
                                >
                                    Submit Another Response
                                </button>
                            </div>
                        </Reveal>
                    ) : (
                        <Reveal delay={0.15}>
                            <form onSubmit={submit} noValidate data-testid="interest-form" className="mt-10 grid gap-5">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="gi-name" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                            Name *
                                        </label>
                                        <input
                                            id="gi-name"
                                            data-testid="interest-name"
                                            type="text"
                                            value={form.name}
                                            onChange={set("name")}
                                            aria-invalid={!!errors.name}
                                            className={`input-field ${errors.name ? "input-error" : ""}`}
                                            placeholder="Your full name"
                                        />
                                        {errors.name && <p className="mt-2 text-xs font-semibold text-[#E8A0A0]" role="alert">{errors.name}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="gi-email" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                            Email *
                                        </label>
                                        <input
                                            id="gi-email"
                                            data-testid="interest-email"
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            aria-invalid={!!errors.email}
                                            className={`input-field ${errors.email ? "input-error" : ""}`}
                                            placeholder="you@example.com"
                                        />
                                        {errors.email && <p className="mt-2 text-xs font-semibold text-[#E8A0A0]" role="alert">{errors.email}</p>}
                                    </div>
                                </div>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="gi-org" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                            Organization
                                        </label>
                                        <input
                                            id="gi-org"
                                            data-testid="interest-organization"
                                            type="text"
                                            value={form.organization}
                                            onChange={set("organization")}
                                            className="input-field"
                                            placeholder="Hotel, university, company…"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="gi-area" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                            I am interested as *
                                        </label>
                                        <select
                                            id="gi-area"
                                            data-testid="interest-area"
                                            value={form.interest_area}
                                            onChange={set("interest_area")}
                                            aria-invalid={!!errors.interest_area}
                                            className={`input-field ${errors.interest_area ? "input-error" : ""}`}
                                        >
                                            <option value="">Select an option…</option>
                                            {INTEREST_AREAS.map((a) => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </select>
                                        {errors.interest_area && <p className="mt-2 text-xs font-semibold text-[#E8A0A0]" role="alert">{errors.interest_area}</p>}
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="gi-message" className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                                        Message
                                    </label>
                                    <textarea
                                        id="gi-message"
                                        data-testid="interest-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={set("message")}
                                        className="input-field resize-y"
                                        placeholder="Tell us a little about your interest (optional)"
                                    />
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        data-testid="interest-submit"
                                        disabled={status === "submitting"}
                                        className="btn-gold disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {status === "submitting" ? "Sending…" : "Submit Interest"}
                                    </button>
                                </div>
                            </form>
                        </Reveal>
                    )}
                </div>
            </section>

            <SupportSection />
        </main>
    );
};

export default GetInvolved;
